import { ApiResponse, ApiResponseMeta } from '@interfaces/api';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { SellingOffersNegotiationData } from '@interfaces/models/negotiation/domain/SellingOffersNegotiation';
import logger from '@helpers/utils/logger/client';
import { datadogRum } from '@datadog/browser-rum-slim';
import {
  SellingOffersProduct,
  SellingOffersProductListResponse,
  SellingOffersProductsItem,
  SellingOffersProductsPagination,
} from '../domain/SellingOffersProduct';
import { SellingOffersProductDataDTO, SellingOffersProductDTO } from '../dtos/SellingOffersProductDTO';

const mapPaginationFromDTO = (meta: ApiResponseMeta): SellingOffersProductsPagination => {
  return {
    limit: meta.limit,
    offset: meta.offset,
    totalCount: meta.count,
  };
};

export const mapSellingOffersProductFromDTO = (product: SellingOffersProductDTO): SellingOffersProduct => {
  return {
    id: product.id,
    headline: product.headline,
    subHeadline: product.subHeadline,
    picturePath: product.picturePath,
    path: product.url,
    price: product.price,
    sellerEarnings: product.sellerEarnings,
  };
};

const mapSellingOffersProductItemFromDTO = (dto: SellingOffersProductDataDTO): SellingOffersProductsItem => {
  const { product, negotiations } = dto;

  return {
    product: mapSellingOffersProductFromDTO(product),
    negotiations: {
      totalNegotiationsCount: negotiations.totalNegotiationsCount,
      pendingSellerActionCount: negotiations.pendingSellerActionCount,
      ongoingNegotiationsCount: negotiations.ongoingNegotiationsCount,
    },
  };
};

export const mapSellingOffersProductListResponse = (
  dto: ApiResponse<SellingOffersProductDataDTO[]>,
): SellingOffersProductListResponse => {
  return {
    items: dto.data.map((dataItem) => mapSellingOffersProductItemFromDTO(dataItem)),
    pagination: mapPaginationFromDTO(dto.meta),
  };
};

export const mapNegotiationToSellingOfferProductsItem = (
  negotiation: Negotiation,
  listItem: SellingOffersProductsItem,
): SellingOffersProductsItem => {
  let err: Error | null = null;

  if (!negotiation) {
    err = new Error('[mapNegotiationToSellingOfferProductsItem]: Cannot map null or undefined negotiation');
  } else if (!listItem?.negotiations) {
    err = new Error(
      '[mapNegotiationToSellingOfferProductsItem]: Cannot map selling offer product  without negotiations in list item',
    );
  }
  if (err) {
    datadogRum.addError(err);
    logger.error(err);
  }

  const { totalNegotiationsCount, ongoingNegotiationsCount, pendingSellerActionCount } = listItem.negotiations;
  const { sellerRemainingOffers, buyerRemainingOffers, isAccepted } = negotiation;

  return {
    ...listItem,
    negotiations: {
      totalNegotiationsCount: totalNegotiationsCount,
      ongoingNegotiationsCount:
        !sellerRemainingOffers && !buyerRemainingOffers ? ongoingNegotiationsCount - 1 : ongoingNegotiationsCount,
      pendingSellerActionCount:
        sellerRemainingOffers > buyerRemainingOffers && !isAccepted
          ? pendingSellerActionCount
          : Math.max(pendingSellerActionCount - 1, 0),
    },
  };
};

export const mapNegotiationToSellingOfferNegotiationData = (
  negotiation: Negotiation,
  listItem: SellingOffersNegotiationData,
): SellingOffersNegotiationData => {
  let err: Error | null = null;

  if (!negotiation) {
    err = new Error('[mapNegotiationToSellingOfferNegotiationData]: Cannot map null or undefined negotiation');
  } else if (!negotiation.lastEvent) {
    err = new Error('[mapNegotiationToSellingOfferNegotiationData]: Cannot map negotiation without lastEvent');
  }
  if (err) {
    datadogRum.addError(err);
    logger.error(err);
  }

  const { lastEvent } = negotiation;
  const isLastEventBuyer = lastEvent.createdBy === 'buyer';

  return {
    ...listItem,
    lastAction: {
      action: lastEvent.action,
      actionNote: lastEvent.userMessage,
      offerFormatted: lastEvent.price.formatted,
      offerSellerEarningsFormatted: lastEvent.sellerPvnPrice?.formatted,
      originator: lastEvent.createdBy,
    },
    negotiationStatus: {
      callerMessage: negotiation.userMessage,
      callerPossibleActions: negotiation.nextActions,
      // caller could be buyer or seller
      callerRemainingActionsNumber: isLastEventBuyer
        ? negotiation.sellerRemainingOffers
        : negotiation.buyerRemainingOffers,
      callerSubMessage: negotiation.userSubMessage,
      isAccepted: negotiation.isAccepted,
      isClosed: negotiation.isClosed,
    },
  };
};
